<template>
    <div class="oauth-callback">
        <div class="oauth-callback__panel">
            <h1 class="oauth-callback__title">{{ title }}</h1>
            <p class="oauth-callback__body">{{ message }}</p>
            <button v-if="showClose" type="button" class="btn btn-secondary btn-sm" @click="closeSelf">
                Close
            </button>
        </div>
    </div>
</template>

<script>
import { MCP_OAUTH_MESSAGE, MCP_OAUTH_STORAGE_KEY } from '../../utils/mcpOauthPopup'

/**
 * Landing page for the MCP OAuth redirect URI.
 *
 * Runs inside the consent popup, which the authorization server has just sent
 * back to our origin. Its entire job is to hand the query parameters to the
 * window that opened it and get out of the way — it deliberately does **not**
 * call the agents service itself. The opener holds the session and the store;
 * duplicating the exchange here would mean a second place that has to get the
 * `iss` forwarding right.
 *
 * Registered as a top-level route rather than under the portal shell: it draws
 * in a 680px popup for well under a second, and there is nothing to navigate.
 *
 * `iss` is forwarded verbatim when present. The service compares it against the
 * issuer recorded at discovery time *before* redeeming the code (RFC 9207).
 * Dropping it here would silently disable the check that closes
 * authorization-server mix-up attacks — which is why it is passed through
 * untouched rather than parsed, normalized, or validated in the browser.
 */
export default {
    name: 'PaiceOAuthCallback',
    data() {
        return {
            title: 'Finishing up',
            message: 'Completing the connection…',
            showClose: false,
        }
    },
    mounted() {
        const params = new URLSearchParams(window.location.search)
        const payload = {
            type: MCP_OAUTH_MESSAGE,
            code: params.get('code'),
            state: params.get('state'),
            // Verbatim. Never normalized here — see the class comment.
            iss: params.get('iss'),
            error: params.get('error'),
            errorDescription: params.get('error_description'),
        }

        if (!payload.error && !payload.code) {
            payload.error = 'invalid_response'
            payload.errorDescription =
                'The authorization server did not return an authorization code.'
        }

        this.report(payload)
    },
    methods: {
        report(payload) {
            const delivered = this.postToOpener(payload)

            if (!delivered) {
                // No opener: the user landed here directly, or a browser policy
                // severed the relationship. Leave the result somewhere the
                // settings page can find it and tell them what to do.
                this.persistForOpener(payload)
                this.title = payload.error ? 'Connection failed' : 'Connection authorized'
                this.message = payload.error
                    ? (payload.errorDescription || payload.error)
                    : 'You can close this window and return to PAICE settings.'
                this.showClose = true
                return
            }

            if (payload.error) {
                this.title = 'Connection failed'
                this.message = payload.errorDescription || payload.error
                this.showClose = true
                return
            }

            // Handed off cleanly. Close on the next tick so the message is
            // actually flushed before the window goes away.
            setTimeout(() => this.closeSelf(), 150)
        },

        postToOpener(payload) {
            const opener = window.opener
            if (!opener || opener.closed) return false
            try {
                // Targeted at our own origin, not '*': the payload carries an
                // authorization code, and a wildcard target would expose it to
                // whatever the opener has since navigated to.
                opener.postMessage(payload, window.location.origin)
                return true
            } catch (e) {
                return false
            }
        },

        persistForOpener(payload) {
            try {
                window.sessionStorage.setItem(MCP_OAUTH_STORAGE_KEY, JSON.stringify(payload))
            } catch (e) {
                // Private browsing or a full quota. The user still sees the
                // message above, which is the part that matters.
            }
        },

        closeSelf() {
            try {
                window.close()
            } catch (e) {
                this.message = 'You can close this window now.'
            }
        },
    },
}
</script>

<style scoped>
.oauth-callback {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem;
    text-align: center;
}

.oauth-callback__panel {
    max-width: 26rem;
}

.oauth-callback__title {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.oauth-callback__body {
    font-size: 0.875rem;
    opacity: 0.8;
    margin-bottom: 1rem;
}
</style>
